// apps/web/src/components/analisis/TablaReportes.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReporteSimplificado } from '../../services/analisisService';
import { formatDateSafe } from '../../utils/dateUtils';

// ===============================
// INTERFACES
// ===============================

interface TablaReportesProps {
  reportes: ReporteSimplificado[];
}

type SortField = 'numero' | 'fecha' | 'maquina' | 'horas' | 'estado';
type SortDirection = 'asc' | 'desc';

// ===============================
// COMPONENTE
// ===============================

export const TablaReportes: React.FC<TablaReportesProps> = ({ reportes }) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField>('fecha');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filtroEstado, setFiltroEstado] = useState<string>('');

  // ===============================
  // FUNCIONES
  // ===============================

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const reportesFiltrados = reportes.filter(reporte => {
    if (!filtroEstado) return true;
    return reporte.estado === filtroEstado;
  });

  const reportesOrdenados = [...reportesFiltrados].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'numero':
        aValue = a.numero;
        bValue = b.numero;
        break;
      case 'fecha':
        aValue = new Date(a.fecha);
        bValue = new Date(b.fecha);
        break;
      case 'maquina':
        aValue = a.maquina?.codigo || '';
        bValue = b.maquina?.codigo || '';
        break;
      case 'horas':
        aValue = a.horas_trabajadas;
        bValue = b.horas_trabajadas;
        break;
      case 'estado':
        aValue = a.estado;
        bValue = b.estado;
        break;
      default:
        return 0;
    }

    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const estadosUnicos = Array.from(new Set(reportes.map(r => r.estado)));

  const getEstadoBadgeClass = (estado: string) => {
    switch (estado) {
      case 'APROBADO':
        return 'bg-green-100 text-green-800';
      case 'PENDIENTE':
        return 'bg-yellow-100 text-yellow-800';
      case 'RECHAZADO':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // ===============================
  // RENDER
  // ===============================

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <div className="bg-gray-50 px-6 py-3 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">📋 {t('reports.title')}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {reportesOrdenados.length} {t('common.of')} {reportes.length} {t('reports.title')}
            </p>
          </div>
          
          {/* Filtro por estado */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">{t('common.filter')}:</label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="text-sm border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">{t('common.all')}</option>
              {estadosUnicos.map(estado => (
                <option key={estado} value={estado}>
                  {estado}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('numero')}
              >
                {t('reports.title')} {getSortIcon('numero')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('fecha')}
              >
                {t('common.date')} {getSortIcon('fecha')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('maquina')}
              >
                {t('reports.machine')} {getSortIcon('maquina')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('horas')}
              >
                {t('common.hours')} {getSortIcon('horas')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('estado')}
              >
                {t('common.status')} {getSortIcon('estado')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                {t('analysis.faenas')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reportesOrdenados.map((reporte) => (
              <tr key={reporte.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {reporte.numero}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDateSafe(reporte.fecha)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {reporte.maquina?.codigo || '-'}
                  {reporte.maquina?.descripcion && (
                    <div className="text-xs text-gray-400 truncate max-w-32">
                      {reporte.maquina.descripcion}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="font-medium">{reporte.horas_trabajadas.toFixed(1)}h</div>
                  {reporte.operador && (
                    <div className="text-xs text-gray-400">
                      {reporte.operador.codigo}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoBadgeClass(reporte.estado)}`}>
                    {reporte.estado}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  <div className="max-w-48">
                    <div className="font-medium">{reporte.total_faenas} {t('analysis.faenas')}</div>
                    {reporte.faenas.length > 0 && (
                      <div className="text-xs text-gray-400 space-y-1 mt-1">
                        {reporte.faenas.slice(0, 2).map((faena, index) => (
                          <div key={index} className="flex justify-between">
                            <span className="truncate mr-2">{faena.faena_codigo}</span>
                            <span className="flex-shrink-0">{faena.area_reporte.toFixed(1)}ha</span>
                          </div>
                        ))}
                        {reporte.faenas.length > 2 && (
                          <div className="text-xs text-gray-400 italic">
                            +{reporte.faenas.length - 2} más
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {reportesOrdenados.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">📋</div>
            <p className="text-gray-500">{t('analysis.no_results')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TablaReportes;